import React, { useState, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faSave,
  faTimes,
  faEye,
  faEyeSlash,
  faUsers,
  faImage,
  faUpload,
  faSpinner,
  faCheck,
  faExclamationTriangle,
  faCalendarWeek,
  faClock,
  faMapMarkerAlt,
} from "@fortawesome/free-solid-svg-icons";
import { supabase } from "../../lib/supabase";
import { useImageUpload } from "../../hooks/useContent";
import ScheduleEditor from "./ScheduleEditor";

const TeamManager = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingMember, setEditingMember] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  
  // Image upload functionality
  const { uploadImage, uploading, error: uploadError } = useImageUpload();
  const [selectedFile, setSelectedFile] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    title: "",
    specialization: "",
    description: "",
    image_url: "",
    phone: "",
    email: "",
    is_active: true,
    qualifications: [],
    availability_schedule: {},
    is_available_for_booking: true,
    booking_notes: "",
    location_ids: [], // Changed from location_id to location_ids array
  });

  useEffect(() => {
    loadTeamMembers();
    loadLocations();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load team members with their associated locations from the junction table
  const loadTeamMembers = async () => {
    setLoading(true);
    try {
      // First get team members
      const { data: teamData, error: teamError } = await supabase
        .from("team_members")
        .select("*")
        .order("order_index", { ascending: true });

      if (teamError) throw teamError;

      // Then get their locations via the junction table
      const { data: teamLocationData, error: teamLocationError } = await supabase
        .from("team_member_locations")
        .select(`
          team_member_id,
          locations (
            id,
            title
          )
        `);

      if (teamLocationError) throw teamLocationError;

      // Combine the data
      const teamMembersWithLocations = teamData.map(member => {
        const memberLocations = teamLocationData
          .filter(tl => tl.team_member_id === member.id)
          .map(tl => tl.locations);
        
        return {
          ...member,
          team_locations: memberLocations,
          // Keep legacy location_id for backward compatibility
          location_id: memberLocations.length > 0 ? memberLocations[0].id : null,
          locations: memberLocations.length > 0 ? memberLocations[0] : null
        };
      });

      setTeamMembers(teamMembersWithLocations || []);
    } catch (error) {
      console.error("Error loading team members:", error);
      showMessage("Eroare la încărcarea echipei", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadLocations = async () => {
    try {
      const { data, error } = await supabase
        .from("locations")
        .select("id, title")
        .eq("is_active", true)
        .order("title", { ascending: true });

      if (error) throw error;
      setLocations(data || []);
    } catch (error) {
      console.error("Error loading locations:", error);
      showMessage("Eroare la încărcarea locațiilor", "error");
    }
  };

  const showMessage = (text, type = "success") => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => {
      setMessage("");
      setMessageType("");
    }, 3000);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        showMessage("Vă rugăm să selectați un fișier imagine valid (jpg, png, webp)", "error");
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        showMessage("Imaginea este prea mare. Vă rugăm să selectați o imagine mai mică de 5MB", "error");
        return;
      }
      
      setSelectedFile(file);
    }
  };

  const handleImageUpload = async () => {
    if (!selectedFile) return;
    
    try {
      const imageUrl = await uploadImage(selectedFile);
      setFormData({
        ...formData,
        image_url: imageUrl
      });
      setSelectedFile(null);
      showMessage("Imagine încărcată cu succes!", "success");
    } catch (error) {
      console.error('Upload error:', error);
      showMessage("Eroare la încărcarea imaginii", "error");
    }
  };

  const clearSelectedFile = () => {
    setSelectedFile(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  // Handle location selection changes
  const handleLocationChange = (locationId) => {
    const currentLocationIds = formData.location_ids || [];
    const isSelected = currentLocationIds.includes(locationId);
    
    if (isSelected) {
      // Remove location
      setFormData({
        ...formData,
        location_ids: currentLocationIds.filter(id => id !== locationId)
      });
    } else {
      // Add location
      setFormData({
        ...formData,
        location_ids: [...currentLocationIds, locationId]
      });
    }
  };

  const handleScheduleChange = useCallback((schedule) => {
    setFormData(prev => ({
      ...prev,
      availability_schedule: schedule,
    }));
  }, []);

  const resetForm = () => {
    setFormData({
      name: "",
      title: "",
      specialization: "",
      description: "",
      image_url: "",
      phone: "",
      email: "",
      is_active: true,
      qualifications: [],
      availability_schedule: {},
      is_available_for_booking: true,
      booking_notes: "",
      location_ids: [],
    });
    setEditingMember(null);
    setShowAddForm(false);
    setSelectedFile(null);
  };

  // Manage team member locations in the junction table
  const manageTeamMemberLocations = async (teamMemberId, locationIds) => {
    // First, remove all existing location assignments
    await supabase
      .from("team_member_locations")
      .delete()
      .eq("team_member_id", teamMemberId);

    // Then, add the new location assignments
    if (locationIds && locationIds.length > 0) {
      const locationAssignments = locationIds.map(locationId => ({
        team_member_id: teamMemberId,
        location_id: locationId
      }));

      const { error } = await supabase
        .from("team_member_locations")
        .insert(locationAssignments);

      if (error) throw error;
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    
    // Check if locations are available
    if (locations.length === 0) {
      showMessage("Nu există locații disponibile. Te rog să adaugi mai întâi o locație în secțiunea 'Locații'.", "error");
      return;
    }
    
    // Validate location selection
    if (!formData.location_ids || formData.location_ids.length === 0) {
      showMessage("Te rog să selectezi cel puțin o locație pentru acest membru din echipă!", "error");
      return;
    }
    
    try {
      const newOrderIndex =
        Math.max(...teamMembers.map((m) => m.order_index || 0), 0) + 1;

      // Prepare data without location_ids for team_members table
      const memberData = { ...formData };
      delete memberData.location_ids;
      memberData.order_index = newOrderIndex;

      const { data, error } = await supabase
        .from("team_members")
        .insert([memberData])
        .select();

      if (error) throw error;

      // Manage location assignments
      await manageTeamMemberLocations(data[0].id, formData.location_ids);

      // Reload data to get the updated team members with locations
      await loadTeamMembers();
      resetForm();
      showMessage("Membru adăugat cu succes!");
    } catch (error) {
      console.error("Error adding team member:", error);
      showMessage("Eroare la adăugarea membrului", "error");
    }
  };

  // Load team member data for editing, including their location assignments
  const handleEditMember = async (member) => {
    try {
      // Get current location assignments for this member
      const { data: locationAssignments, error } = await supabase
        .from("team_member_locations")
        .select("location_id")
        .eq("team_member_id", member.id);

      if (error) throw error;

      const locationIds = locationAssignments.map(la => la.location_id);

      setFormData({
        name: member.name || "",
        title: member.title || "",
        specialization: member.specialization || "",
        description: member.description || "",
        image_url: member.image_url || "",
        phone: member.phone || "",
        email: member.email || "",
        is_active: member.is_active !== false,
        qualifications: member.qualifications || [],
        availability_schedule: member.availability_schedule || {},
        is_available_for_booking: member.is_available_for_booking !== false,
        booking_notes: member.booking_notes || "",
        location_ids: locationIds,
      });
      setEditingMember(member.id);
      setShowAddForm(false);
    } catch (error) {
      console.error("Error loading member for edit:", error);
      showMessage("Eroare la încărcarea datelor pentru editare", "error");
    }
  };

  const handleUpdateMember = async (e) => {
    e.preventDefault();
    
    // Check if locations are available
    if (locations.length === 0) {
      showMessage("Nu există locații disponibile. Te rog să adaugi mai întâi o locație în secțiunea 'Locații'.", "error");
      return;
    }
    
    // Validate location selection
    if (!formData.location_ids || formData.location_ids.length === 0) {
      showMessage("Te rog să selectezi cel puțin o locație pentru acest membru din echipă!", "error");
      return;
    }
    
    try {
      // Prepare data without location_ids for team_members table
      const memberData = { ...formData };
      delete memberData.location_ids;

      const { error } = await supabase
        .from("team_members")
        .update(memberData)
        .eq("id", editingMember);

      if (error) throw error;

      // Manage location assignments
      await manageTeamMemberLocations(editingMember, formData.location_ids);

      // Reload data to get the updated team members with locations
      await loadTeamMembers();
      resetForm();
      showMessage("Membru actualizat cu succes!");
    } catch (error) {
      console.error("Error updating team member:", error);
      showMessage("Eroare la actualizarea membrului", "error");
    }
  };

  const handleDeleteMember = async (memberId) => {
    if (!window.confirm("Sigur doriți să ștergeți acest membru?")) return;

    try {
      // Delete team member (cascade will handle location relationships)
      const { error } = await supabase
        .from("team_members")
        .delete()
        .eq("id", memberId);

      if (error) throw error;

      // Reload data to ensure consistency
      await loadTeamMembers();
      showMessage("Membru șters cu succes!");
    } catch (error) {
      console.error("Error deleting team member:", error);
      showMessage("Eroare la ștergerea membrului", "error");
    }
  };

  const handleToggleActive = async (memberId, currentStatus) => {
    try {
      const { error } = await supabase
        .from("team_members")
        .update({ is_active: !currentStatus })
        .eq("id", memberId);

      if (error) throw error;

      // Reload data to ensure consistency
      await loadTeamMembers();
      showMessage(`Membru ${!currentStatus ? "activat" : "dezactivat"}!`);
    } catch (error) {
      console.error("Error toggling member status:", error);
      showMessage("Eroare la schimbarea statusului", "error");
    }
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Se încarcă...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div className="header-content">
          <FontAwesomeIcon icon={faUsers} className="header-icon" />
          <div>
            <h1>Manager Echipa Medicală</h1>
            <p>Gestionează membrii echipei medicale</p>
          </div>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => setShowAddForm(true)}
        >
          <FontAwesomeIcon icon={faPlus} /> Adaugă Membru
        </button>
      </div>

      {message && (
        <div className={`alert alert-${messageType === "error" ? "danger" : "success"}`}>
          {message}
        </div>
      )}

      {(showAddForm || editingMember) && (
        <div className="admin-form-container">
          <form onSubmit={editingMember ? handleUpdateMember : handleAddMember}>
            <div className="form-header">
              <h3>
                {editingMember ? "Editează Membru" : "Adaugă Membru Nou"}
              </h3>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={resetForm}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Nume complet</label>
                  <input
                    type="text"
                    className="form-control"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Titlu/Funcție</label>
                  <input
                    type="text"
                    className="form-control"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="ex: Medic Specialist Pneumolog"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Specializare</label>
                  <input
                    type="text"
                    className="form-control"
                    name="specialization"
                    value={formData.specialization}
                    onChange={handleInputChange}
                    placeholder="ex: Pneumologie, Medicina Muncii"
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Imagine Profil</label>
                  
                  {/* Show current image if exists */}
                  {formData.image_url && (
                    <div className="mb-2">
                      <img 
                        src={formData.image_url} 
                        alt="Preview" 
                        className="img-thumbnail"
                        style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                      />
                      <div className="mt-1">
                        <small className="text-success">
                          <FontAwesomeIcon icon={faCheck} className="me-1" />
                          Imagine încărcată cu succes
                        </small>
                      </div>
                    </div>
                  )}

                  {/* File input */}
                  <div className="input-group">
                    <input
                      type="file"
                      className="form-control"
                      accept="image/*"
                      onChange={handleFileSelect}
                      disabled={uploading}
                    />
                    {selectedFile && (
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleImageUpload}
                        disabled={uploading}
                      >
                        {uploading ? (
                          <>
                            <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
                            Se încarcă...
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faUpload} className="me-1" />
                            Încarcă
                          </>
                        )}
                      </button>
                    )}
                  </div>

                  {/* Selected file info */}
                  {selectedFile && (
                    <div className="mt-2">
                      <small className="text-info">
                        <FontAwesomeIcon icon={faImage} className="me-1" />
                        Fișier selectat: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                      </small>
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 ms-2"
                        onClick={clearSelectedFile}
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </div>
                  )}

                  {/* Upload error */}
                  {uploadError && (
                    <div className="mt-2">
                      <small className="text-danger">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                        Eroare la încărcare: {uploadError.message}
                      </small>
                    </div>
                  )}

                  {/* Manual URL input as fallback */}
                  <div className="mt-2">
                    <details>
                      <summary className="text-muted small" style={{ cursor: 'pointer' }}>
                        Sau introduceți manual URL-ul imaginii
                      </summary>
                      <input
                        type="url"
                        className="form-control form-control-sm mt-2"
                        name="image_url"
                        value={formData.image_url}
                        onChange={handleInputChange}
                        placeholder="https://..."
                      />
                    </details>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label">Descriere</label>
              <textarea
                className="form-control"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="3"
                placeholder="Scurtă descriere despre experiența și specializarea medicului"
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Calificări</label>
              <small className="text-muted d-block mb-2">
                Calificările vor fi afișate cu icoane pe site (prima cu stetoscop, următoarele cu diplomă)
              </small>
              {formData.qualifications.map((qualification, index) => (
                <div key={index} className="input-group mb-2">
                  <input
                    type="text"
                    className="form-control"
                    value={qualification}
                    onChange={(e) => {
                      const newQualifications = [...formData.qualifications];
                      newQualifications[index] = e.target.value;
                      setFormData({ ...formData, qualifications: newQualifications });
                    }}
                    placeholder={index === 0 ? "Ex: Medic Primar Pneumolog" : "Ex: Competențe specifice"}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    onClick={() => {
                      const newQualifications = formData.qualifications.filter((_, i) => i !== index);
                      setFormData({ ...formData, qualifications: newQualifications });
                    }}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={() => {
                  setFormData({ 
                    ...formData, 
                    qualifications: [...formData.qualifications, ""] 
                  });
                }}
              >
                <FontAwesomeIcon icon={faPlus} className="me-1" />
                Adaugă Calificare
              </button>
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Telefon</label>
                  <input
                    type="tel"
                    className="form-control"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="0700 000 000"
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-control"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="doctor@alicranta.ro"
                  />
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                                      <label className="form-label">
                      Locații de lucru <span className="text-danger">*</span>
                    </label>
                    <div className="location-checkboxes">
                      {locations.length === 0 ? (
                        <div className="alert alert-warning">
                          Nu există locații disponibile. Te rog să adaugi mai întâi o locație în secțiunea 'Locații'.
                        </div>
                      ) : (
                        <div className="row">
                          {locations.map((location) => (
                            <div key={location.id} className="col-md-6 mb-2">
                                                              <div className="form-check p-2 border rounded" style={{backgroundColor: formData.location_ids.includes(location.id) ? '#e3f2fd' : '#f8f9fa'}}>
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={`location-${location.id}`}
                                    value={location.id}
                                    checked={formData.location_ids.includes(location.id)}
                                    onChange={() => handleLocationChange(location.id)}
                                  />
                                  <label className="form-check-label w-100" htmlFor={`location-${location.id}`} style={{cursor: 'pointer'}}>
                                    <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2 text-info" />
                                    {location.title}
                                  </label>
                                </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  <small className="form-text text-muted">
                    {locations.length === 0 
                      ? "Nu există locații disponibile. Te rog să adaugi mai întâi o locație în secțiunea 'Locații'." 
                      : "Este obligatoriu să selectezi cel puțin o locație unde lucrează acest membru"
                    }
                  </small>
                </div>
              </div>
            </div>

            <div className="mb-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                />
                <label className="form-check-label">
                  Membru activ (vizibil pe site)
                </label>
              </div>
            </div>

            <div className="mb-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="is_available_for_booking"
                  checked={formData.is_available_for_booking}
                  onChange={handleInputChange}
                />
                <label className="form-check-label">
                  <FontAwesomeIcon icon={faCalendarWeek} className="me-1" />
                  Disponibil pentru programări online
                </label>
              </div>
            </div>

            {formData.is_available_for_booking && (
              <>
                <div className="mb-3">
                  <label className="form-label">Note programări</label>
                  <textarea
                    className="form-control"
                    name="booking_notes"
                    value={formData.booking_notes}
                    onChange={handleInputChange}
                    rows="2"
                    placeholder="Informații suplimentare pentru programări (ex: specializări, instrucțiuni speciale)"
                  />
                </div>

                <div className="mb-4">
                  <ScheduleEditor
                    schedule={formData.availability_schedule}
                    onChange={handleScheduleChange}
                    disabled={false}
                  />
                </div>
              </>
            )}

            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                <FontAwesomeIcon icon={faSave} />{" "}
                {editingMember ? "Actualizează" : "Adaugă"}
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={resetForm}
              >
                Anulează
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="admin-content">
        <div className="row g-3">
          {teamMembers.map((member) => (
            <div key={member.id} className="col-lg-4 col-md-6">
              <div className="card h-100 team-member-card">
                <div className="card-header d-flex justify-content-between align-items-center py-2">
                  <span className={`badge ${member.is_active ? 'bg-success' : 'bg-secondary'}`}>
                    {member.is_active ? 'ACTIV' : 'INACTIV'}
                  </span>
                  <div className="team-action-buttons">
                    <button
                      className={`team-action-btn ${member.is_active ? 'btn-toggle-active' : 'btn-toggle-inactive'}`}
                      onClick={() => handleToggleActive(member.id, member.is_active)}
                      title={member.is_active ? "Dezactivează" : "Activează"}
                    >
                      <FontAwesomeIcon icon={member.is_active ? faEyeSlash : faEye} />
                    </button>
                    <button
                      className="team-action-btn btn-edit"
                      onClick={() => handleEditMember(member)}
                      title="Editează"
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button
                      className="team-action-btn btn-delete"
                      onClick={() => handleDeleteMember(member.id)}
                      title="Șterge"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </div>

                <div className="card-body p-3">
                  <div className="row">
                    {member.image_url && (
                      <div className="col-4">
                        <img 
                          src={member.image_url} 
                          alt={member.name} 
                          className="img-fluid rounded"
                          style={{maxHeight: '80px', objectFit: 'cover'}}
                        />
                      </div>
                    )}
                    <div className={member.image_url ? "col-8" : "col-12"}>
                      <h6 className="card-title mb-1">{member.name}</h6>
                      <p className="text-muted small mb-1">{member.title}</p>
                      {member.specialization && (
                        <p className="small mb-1">
                          <strong>Specializare:</strong>{" "}
                          {typeof member.specialization === 'string' 
                            ? member.specialization 
                            : (member.specialization?.description || 'Complex object')
                          }
                        </p>
                      )}
                      {member.qualifications && member.qualifications.length > 0 && (
                        <p className="small mb-1">
                          <strong>Calificări:</strong>{" "}
                          {member.qualifications.join(", ")}
                        </p>
                      )}
                      {member.team_locations && member.team_locations.length > 0 && (
                        <div className="mb-1">
                          <strong className="small d-block">Locații:</strong>
                          <div className="d-flex flex-wrap gap-1">
                            {member.team_locations.map((location, index) => (
                              <span key={index} className="badge bg-info text-dark">
                                <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" />
                                {location.title}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {member.description && (
                    <p className="small text-muted mt-2 mb-2" style={{fontSize: '0.8rem'}}>
                      {member.description.length > 100 
                        ? member.description.substring(0, 100) + '...' 
                        : member.description
                      }
                    </p>
                  )}
                  
                  <div className="d-flex flex-wrap gap-1 mt-2">
                    {member.phone && (
                      <span className="badge bg-light text-dark small">
                        📞 {member.phone}
                      </span>
                    )}
                    {member.email && (
                      <span className="badge bg-light text-dark small">
                        ✉️ {member.email}
                      </span>
                    )}
                    {member.is_available_for_booking && (
                      <span className="badge bg-success text-white small">
                        <FontAwesomeIcon icon={faCalendarWeek} className="me-1" />
                        Programări Online
                      </span>
                    )}
                  </div>

                  {member.is_available_for_booking && member.availability_schedule && (
                    <div className="mt-2">
                      <small className="text-muted">
                        <FontAwesomeIcon icon={faClock} className="me-1" />
                        <strong>Program:</strong>
                      </small>
                      <div className="schedule-preview mt-1">
                        {Object.entries(member.availability_schedule || {}).map(([day, slots]) => {
                          if (!slots || slots.length === 0) return null;
                          const dayNames = {
                            monday: 'Luni',
                            tuesday: 'Marți', 
                            wednesday: 'Miercuri',
                            thursday: 'Joi',
                            friday: 'Vineri',
                            saturday: 'Sâmbătă',
                            sunday: 'Duminică'
                          };
                          return (
                            <div key={day} className="small text-muted">
                              <strong>{dayNames[day]}:</strong> {slots.map(slot => `${slot.start}-${slot.end}`).join(', ')}
                            </div>
                          );
                        })}
                      </div>
                      {member.booking_notes && (
                        <div className="mt-1">
                          <small className="text-info">
                            <FontAwesomeIcon icon={faCheck} className="me-1" />
                            {member.booking_notes}
                          </small>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {member.social_links && typeof member.social_links === 'object' && (
                    <div className="mt-2">
                      <span className="badge bg-warning text-dark small">
                        ⚠️ Object field needs cleanup
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {teamMembers.length === 0 && (
          <div className="col-12">
            <div className="text-center py-5">
              <FontAwesomeIcon icon={faUsers} size="3x" className="text-muted mb-3" />
              <h4>Niciun membru în echipă</h4>
              <p className="text-muted">Adaugă primul membru al echipei medicale.</p>
              <button
                className="btn btn-primary"
                onClick={() => setShowAddForm(true)}
              >
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Adaugă Primul Membru
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamManager; 